import { useEffect, useState, useMemo, useRef } from 'react';
import cm from 'classnames';

import { Error } from 'components/app/_helpers/app-errors.helper';
import { useAppSelector } from 'store/store-hooks';
import { useLocation, Path } from 'components/_hooks/use-router';
import { selectClipData } from 'components/app/_helpers/_app.helper';
import { useCssRotate } from 'components/app/_mediator/css-rotate/use-css-rotate';
import { useMousePositionContext } from 'components/app/_mediator/app-cursor-position.provider';
import { useClipThumbContext } from 'components/app/_mediator/clip-thumb/clip-thumb.provider';
import { useClipThumbPrevContext } from 'components/app/_mediator/clip-thumb-prev.provider';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';
import { FcClip } from 'components/app/finalcuts/_mediator/fc-data.slice';

import { ClipLabels } from 'components/app/app-components/clip-labels/clip-labels';
import styles from './_clip-thumb-prev.module.scss';

export const ClipThumbPrev = () => {
  const { mousePosition } = useMousePositionContext();
  const { resetHoveredClip, hoveredClip } = useClipThumbPrevContext();
  const { getThumbnailSrc } = useClipThumbContext();
  const { path } = useLocation();

  const container = useRef<HTMLDivElement>(null);
  const cssContainer = useRef(null);

  const dragger = useAppSelector((state) => state.app.dragger);
  const fcErrorClips = useAppSelector((state) => state.fc__data.fcErrorClips);
  const activeMenuClip = useAppSelector((state) => state.editor.activeMenuClip);
  const clipThumbnails = useAppSelector((state) => state.clipThumb.clipThumbnails);
  const fcFileConfigsMap = useAppSelector((state) => state.fc__data.fcFileConfigsMap);
  const hideTooltipListener = useAppSelector((state) => state.app.hideTooltipListener);
  const cellResizeHighlight = useAppSelector((state) => state.sb.cellResizeHighlight);
  const errorRateMap = useAppSelector((state) => state.sb.errorRateMap);
  const fcClipsMap = useAppSelector((state) => state.fc__data.fcClipsMap);
  const configs = useAppSelector((state) => state.app__files.configs);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);

  const [isDragging, setIsDragging] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const isFc = useMemo(() => {
    return path === Path.finalcuts;
  }, [path]);

  const clipId = useMemo(() => {
    return hoveredClip.current;
  }, [mousePosition]);

  const clipData = useMemo(() => {
    if (!clipId || !clipsMap) return;
    return selectClipData(clipId, isFc ? fcClipsMap : clipsMap);
  }, [clipId]);

  const src = useMemo(() => {
    if (!clipId || clipId === EMPTY_CELL) return '';
    if (isFc) {
      const clipData = selectClipData(clipId, fcClipsMap) as FcClip;
      if (clipData.thumbnailUrl) return clipData.thumbnailUrl;
    }
    return getThumbnailSrc(clipId, clipThumbnails);
  }, [clipId, clipId && clipThumbnails[clipId]]);

  const isVisible = useMemo(() => {
    return clipId && !isDragging && !activeMenuClip && !isClicked && !cellResizeHighlight;
  }, [clipId, activeMenuClip, isClicked, isDragging, cellResizeHighlight]);

  const tooltipCssEffects = useMemo(() => {
    if (!clipId) return;
    const relevantClip = isFc ? fcClipsMap?.[clipId] : clipsMap?.[clipId];
    const relevantConfigs = isFc ? fcFileConfigsMap : configs;
    return {
      clipData: relevantClip || undefined,
      fileConfig: (relevantClip && relevantConfigs[relevantClip.mediaFileId]) || undefined,
    };
  }, [configs, clipId, fcFileConfigsMap]);

  useCssRotate(cssContainer, { ...tooltipCssEffects });

  const clipError = useMemo(() => {
    if (!clipId) return null;
    switch (path) {
      case Path.storyboard:
        return errorRateMap[clipId] ? Error.invalidClipPlaybackRate(errorRateMap[clipId]) : null;
      case Path.finalcuts:
        return fcErrorClips[clipId];
    }
  }, [errorRateMap, clipId]);

  useEffect(() => {
    return () => {
      resetHoveredClip();
    };
  }, []);

  useEffect(() => {
    setIsDragging(!!dragger);
  }, [dragger]);

  useEffect(() => {
    setIsClicked(true);
  }, [hideTooltipListener]);

  useEffect(() => {
    setIsClicked(false);
  }, [src]);

  if (clipId === EMPTY_CELL) return null;

  return (
    <div
      ref={container}
      style={{ left: mousePosition.x, top: mousePosition.y }}
      className={cm(styles.ThumbnailTooltip, {
        [styles['ThumbnailTooltip--active']]: isVisible,
      })}
    >
      <div className={styles.ThumbnailTooltip__container}>
        {clipError ? (
          <div className={styles.ThumbnailTooltip__error}>{clipError}</div>
        ) : (
          clipData && <ClipLabels containerRef={container} clipData={clipData} />
        )}
        <div className={styles.ThumbnailTooltip__img} ref={cssContainer}>
          <img src={src} crossOrigin="anonymous" />
        </div>
      </div>
    </div>
  );
};
